// Generic "where was the reporter" page-context detection, shared by every
// reporting surface (Incident Report + Support/Helpdesk). Detect WHICH
// entity/page the reporter was on, entirely client-side, and build the
// Reference link for tables.
//
// Nothing is invented: every field is read from window.location / document /
// navigator. Absent signals are simply omitted. What a caller persists is a
// generic entity reference (entityType + entityId), the exact applicationUrl,
// and this whole object as page_context jsonb (best-effort audit trail).

export type ReportEntityKind =
  | 'itinerary'
  | 'product'
  | 'operation'
  | 'preview'
  | 'other';

export type ReportPageContext = {
  /** Full URL incl. query params — the exact navigation target. */
  href?: string;
  pathname?: string;
  search?: string;
  /** Query params as structured data. */
  query?: Record<string, string>;
  /** Friendly area label (Planner, Pricing, Products, Operations, …). */
  module?: string;
  /** Wire field name mirrored from `module` (kept for reporting callers). */
  sourceModule?: string;
  route?: string;
  /** Active sub-tab / pane (from ?pane / ?tab). */
  tab?: string;
  title?: string;
  entityType?: ReportEntityKind;
  entityId?: string;
  itineraryId?: string;
  productId?: string;
  operationId?: string;
  accountId?: string;
  capturedAt?: string;
  userAgent?: string;
  language?: string;
  viewport?: string;
  /** Latest visible error (ARIA alert), if any. */
  visibleError?: string;
};

const ITINERARY_DETAIL = /^\/itineraries\/[^/]+/;

// Map a route to a friendly area label the reporter recognises. Recognises
// both reporting-surface management routes so a ticket/incident raised from
// either page is labelled accurately.
export const moduleLabelForRoute = (route: string): string => {
  if (!route || route === '/') return 'Xthory';
  if (route.startsWith('/itineraries/create')) return 'New itinerary';
  if (ITINERARY_DETAIL.test(route)) {
    if (/\/prices(\/|$)/.test(route)) return 'Pricing';
    if (/\/settings(\/|$)/.test(route)) return 'Itinerary Settings';
    if (/\/payments(\/|$)/.test(route)) return 'Payments';
    return 'Planner';
  }
  if (route.startsWith('/itineraries')) return 'Itinerary Table';
  if (
    route.startsWith('/operations-dashboard') ||
    route.startsWith('/ops-dashboard')
  ) {
    return 'Ops Dashboard';
  }
  if (route.startsWith('/operations')) return 'Operations';
  if (route.startsWith('/preview')) return 'Preview';
  if (route.startsWith('/products')) return 'Products';
  if (route.startsWith('/crm')) return 'CRM';
  if (route.startsWith('/moodboard')) return 'Moodboard';
  if (route.startsWith('/dashboard')) return 'Dashboard';
  if (route.startsWith('/inbox')) return 'Inbox';
  if (route.startsWith('/messaging')) return 'Messaging';
  if (route.startsWith('/incidents-management')) return 'Incidents';
  if (route.startsWith('/support-management')) return 'Support';
  return 'Xthory';
};

const UUID = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}';

// Detect the id-bearing entity from a pathname. Order matters — product /
// preview / operation before the generic itinerary detail.
export const detectEntity = (
  pathname: string,
  query: Record<string, string> = {}
): { entityType: ReportEntityKind; entityId?: string } => {
  const product = pathname.match(new RegExp(`^/products/(${UUID})`, 'i'));
  if (product) return { entityType: 'product', entityId: product[1] };

  const preview = pathname.match(new RegExp(`^/preview/(${UUID})`, 'i'));
  if (preview) return { entityType: 'preview', entityId: preview[1] };

  const operation = pathname.match(new RegExp(`^/operations/(${UUID})`, 'i'));
  if (operation) return { entityType: 'operation', entityId: operation[1] };

  const itinerary = pathname.match(new RegExp(`^/itineraries/(${UUID})`, 'i'));
  if (itinerary) return { entityType: 'itinerary', entityId: itinerary[1] };

  // Best-effort: an operation surface that carries its id in the query.
  if (pathname.startsWith('/operations') && query.operationId) {
    return { entityType: 'operation', entityId: query.operationId };
  }
  return { entityType: 'other' };
};

const detectVisibleError = (): string | undefined => {
  if (typeof document === 'undefined') return undefined;
  // ARIA alert = the app's reliable "something went wrong" surface (toasts,
  // inline error banners). Deliberately narrow — never scrape arbitrary text.
  const el = document.querySelector('[role="alert"]');
  const text = el?.textContent?.trim();
  return text && text.length > 0 && text.length <= 300 ? text : undefined;
};

// Capture the current page context. `extra.accountId` is threaded from auth
// (navigator can't give it). Safe on the server (returns {}).
export const detectPageContext = (extra?: {
  accountId?: string;
}): ReportPageContext => {
  if (typeof window === 'undefined') return {};
  const { pathname, search, href } = window.location;

  const query: Record<string, string> = {};
  for (const [k, v] of new URLSearchParams(search)) query[k] = v;

  const module = moduleLabelForRoute(pathname);
  const { entityType, entityId } = detectEntity(pathname, query);

  const ctx: ReportPageContext = {
    href,
    pathname,
    search: search || undefined,
    query: Object.keys(query).length ? query : undefined,
    module,
    sourceModule: module,
    route: pathname,
    tab: query.pane ?? query.tab ?? undefined,
    title:
      typeof document !== 'undefined' ? document.title || undefined : undefined,
    entityType,
    entityId,
    accountId: extra?.accountId,
    capturedAt: new Date().toISOString(),
    userAgent: navigator.userAgent,
    language: navigator.language,
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    visibleError: detectVisibleError(),
  };
  if (entityType === 'itinerary' && entityId) ctx.itineraryId = entityId;
  if (entityType === 'product' && entityId) ctx.productId = entityId;
  if (entityType === 'operation' && entityId) ctx.operationId = entityId;
  return ctx;
};

const UUID_EXACT = new RegExp(`^${UUID}$`, 'i');
const ITINERARY_LINK = new RegExp(`/itineraries/(${UUID})`, 'i');

// Scan the current page for itineraries the reporter could link, so a
// "Link an itinerary" picker is pre-seeded (no typing needed). Two reliable
// signals, deduped by id: `[data-itinerary-id]` markers (e.g. operation cards)
// and `/itineraries/:uuid` links (lists, dashboards, CRM). The ids are
// resolved to references server-side; nothing is invented. Capped to 100.
export const detectPageItineraryIds = (): string[] => {
  if (typeof document === 'undefined') return [];
  const ids = new Set<string>();

  for (const el of document.querySelectorAll('[data-itinerary-id]')) {
    const id = el.getAttribute('data-itinerary-id');
    if (id && UUID_EXACT.test(id)) ids.add(id.toLowerCase());
  }
  for (const anchor of document.querySelectorAll('a[href*="/itineraries/"]')) {
    const match = (anchor.getAttribute('href') ?? '').match(ITINERARY_LINK);
    if (match) ids.add(match[1].toLowerCase());
  }

  return [...ids].slice(0, 100);
};

// ---- Reference (Reference column + row navigation) ----

export type ReportReference = {
  kind: ReportEntityKind;
  label: string;
  /** Best navigation target: the stored full URL, else a safe built URL. */
  url: string | null;
};

// A raw report shape (own OR full) — only the reference-relevant fields.
type ReferenceSource = {
  entityType?: string | null;
  entityId?: string | null;
  itineraryId?: string | null;
  itineraryRef?: string | null;
  applicationUrl?: string | null;
  pageContext?: { module?: string } | null;
};

const pathOf = (url?: string | null): string => (url ? url.split('?')[0] : '');

// Build the Reference {kind,label,url} for a table row. Prefers the stored
// applicationUrl (exact original URL incl. query); falls back to a safe URL
// built from the entity id; falls back to the module label + no link.
export const buildReference = (inc: ReferenceSource): ReportReference => {
  const url = inc.applicationUrl ?? null;
  const kind: ReportEntityKind =
    (inc.entityType as ReportEntityKind) ??
    (inc.itineraryId ? 'itinerary' : 'other');

  if (kind === 'product') {
    return {
      kind,
      label: 'Product',
      url: url ?? (inc.entityId ? `/products/${inc.entityId}/edit` : null),
    };
  }
  if (kind === 'itinerary') {
    const id = inc.itineraryId ?? inc.entityId ?? null;
    return {
      kind,
      label: inc.itineraryRef ?? 'Itinerary',
      url: url ?? (id ? `/itineraries/${id}` : null),
    };
  }
  if (kind === 'operation') {
    return { kind, label: 'Operation', url: url ?? '/operations' };
  }
  if (kind === 'preview') {
    return {
      kind,
      label: 'Preview',
      url: url ?? (inc.entityId ? `/preview/${inc.entityId}` : null),
    };
  }
  // other — use the captured module label, else derive it from the stored URL.
  const label =
    inc.pageContext?.module ??
    (url ? moduleLabelForRoute(pathOf(url)) : 'Page');
  return { kind: 'other', label, url };
};
